/*
 * Redirects the retired visa-keyed case URLs (`/individual/:id/:visaType/...`)
 * onto the client-scoped scheme. The client is not in the old URL, so the matter
 * is fetched once to find it; cases with no client link land on `/cases/:id`.
 */
import { type FC, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { LoadingState } from "@/components/brand";

import { MattersAPI } from "../../api/matters";
import {
  type CaseRef,
  caseWorkspacePath,
  parseCasePath,
  rehomeCasePath,
  retiredSegmentsToWorkspace
} from "./routes";

/**
 * Old per-draft-type "edit" links (`drafts/cover-letter/edit`) named a draft
 * type, not a draft id, so they cannot open a specific draft; they land on the
 * workspace like the Drafts tab they used to redirect to.
 */
const LEGACY_DRAFT_TYPE_SLUGS = new Set([
  "cover-letter",
  "legal-brief",
  "rfe-response",
  "noid-noir-response",
  "personal-statement",
  "certified-statement",
  "affidavit",
  "hardship-statement-beneficiary",
  "hardship-statement-petitioner",
  "exhibit-list",
  "expert-letters"
]);

/** Pure: where a legacy case path goes once the case's client is known. */
export const legacyCaseTarget = (
  pathname: string,
  search: string,
  ref: CaseRef
): string | null => {
  const parsed = parseCasePath(pathname);
  if (!parsed) return null;
  const { segments } = parsed;
  if (segments[0] === "drafts" && LEGACY_DRAFT_TYPE_SLUGS.has(segments[1])) {
    return caseWorkspacePath(ref);
  }
  return (
    retiredSegmentsToWorkspace(ref, segments) ??
    rehomeCasePath(pathname, search, ref)
  );
};

export const LegacyCaseRedirect: FC = () => {
  const { pathname, search } = useLocation();
  const individualId = parseCasePath(pathname)?.ref.individualId ?? "";
  const [ref, setRef] = useState<CaseRef | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRef(null);
    if (!individualId) return undefined;
    (async () => {
      let foreignNationalId: string | null = null;
      try {
        const matter = await MattersAPI.get(individualId);
        foreignNationalId =
          (matter.individual?.foreign_national_id ?? "").trim() || null;
      } catch {
        // Unknown or unreadable matter: still move the link onto the new
        // scheme; the case shell reports the real error.
      }
      if (!cancelled) setRef({ individualId, foreignNationalId });
    })();
    return () => {
      cancelled = true;
    };
  }, [individualId]);

  if (!individualId) return <Navigate to="/" replace />;
  if (!ref) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <LoadingState size="xl" text="Opening case…" />
      </div>
    );
  }
  return (
    <Navigate to={legacyCaseTarget(pathname, search, ref) ?? "/"} replace />
  );
};
